import { keyOf } from '@mds/shared/lib/dates';

/** Download helpers for report exports. */

/**
 * Serialise rows to CSV. Quotes any field containing a comma, quote or newline,
 * and prefixes a UTF-8 BOM so Excel reads accented names (Café) correctly.
 */
export function toCsv(rows: (string | number)[][]): string {
  const esc = (v: string | number) => {
    const s = String(v ?? '');
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return '﻿' + rows.map((r) => r.map(esc).join(',')).join('\r\n');
}

/** Long enough for the browser to have started the download before we revoke. */
const REVOKE_DELAY_MS = 1000;

/** Trigger a client-side file download. */
export function download(filename: string, contents: BlobPart, type = 'text/csv;charset=utf-8') {
  const url = URL.createObjectURL(new Blob([contents], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), REVOKE_DELAY_MS);
}

/**
 * Export to PDF via the browser's print dialog ("Save as PDF").
 *
 * Deliberately not a PDF library: jsPDF + html2canvas would add ~550KB and
 * rasterise the charts, while printing keeps the SVG vector-crisp, selectable
 * and correct at any paper size. `index.css` carries the print rules and
 * `components/reporting/print-frame.tsx` the header on every page.
 *
 * The document title is swapped for the report's name while the dialog is
 * open, because that is what the browser proposes as the PDF's filename.
 */
export function printReport(title: string) {
  const previous = document.title;
  document.title = title;
  const restore = () => {
    document.title = previous;
    window.removeEventListener('afterprint', restore);
  };
  window.addEventListener('afterprint', restore);
  window.print();
}

/**
 * Print only the open dialog, the same way `printReport` prints a page. The
 * `print-dialog` class switches on the rules in `index.css` that drop the page
 * behind the dialog and unfold the dialog itself onto paper.
 */
export function printDialog(title: string) {
  const root = document.documentElement;
  root.classList.add('print-dialog');
  window.addEventListener('afterprint', () => root.classList.remove('print-dialog'), {
    once: true,
  });
  printReport(title);
}

/** Today's date as `yyyy-mm-dd`, for stamping an export filename. */
export const stamp = (d = new Date()) => keyOf(d);
